import React, { useCallback, useState } from 'react';
import { ComponentType } from 'react';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import CardContent from '@mui/material/CardContent';
import CardActions from '@mui/material/CardActions';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Chip,
  Stack,
} from '@mui/material';
import api from '../../../services/api';
import { ConfirmationTable } from '../../../modules/accommodation-provider/components/ConfirmationTable';
import {AttachFileRounded, ExpandMoreRounded} from '@mui/icons-material';
import { ConfirmedTable } from '../../../modules/accommodation-provider/components/ConfirmedTable';
import { MoreOptions } from './MoreOptions';

interface Props {
  confirmed?: boolean;
  past?: number;
  item: any;
  onRefresh: () => void;
  onOpenChat: (row: any) => void;
  setOpenConfirmDialog: (row: any) => void;
  setOpenFileUploadDialog: (row: any) => void;
}

export const ServiceReservationConfirmedCard: ComponentType<Props> = ({
  item,
  onRefresh,
  confirmed,
  past,
  onOpenChat,
  setOpenConfirmDialog,
  setOpenFileUploadDialog,
}) => {
  const [buttonLoading, setButtonLoading] = useState<any>(false);
  const [selectedActionType, setSelectedActionType] = useState<any>(false);
  const [openOffer, setOpenOffer] = useState(false);
  const [offers, setOffers] = useState<any>([]);

  const onSubmit = useCallback(
    (
      action: 'accept' | 'decline' | 'offer',
      reservationId: any,
      confirmationId: any,
      offers?: any
    ) => {
      setButtonLoading(action);
      setSelectedActionType(action);
      api
        .post(
          `/accommodation-provider/accommodation-reservation-confirmations/${reservationId}/${confirmationId}`,
          {
            action: action,
            offers: offers,
          }
        )
        .then((result: any) => {
          onRefresh();
        })
        .catch((error: any) => {})
        .finally(() => {
          setButtonLoading(false);
        });
    },
    [selectedActionType]
  );

  const handleOffer = (modification: any) => {
    let date = '2025-02-10';
    if (modification.raw_formatted?.date) {
      date = modification.formatted.date
        .replace(/\s*\(.*?\)/, '')
        .replace(/\./g, '-')
        .slice(0, -1);
    }

    let time = '11:00';
    if (modification.raw_formatted?.start_time) {
      time = modification.raw_formatted.start_time;
    }

    setOpenOffer(true);
    setOffers([
      {
        date: date,
        time: time,
      },
    ]);
  };

  const type = item?.type;

  const implodedType = type.split('→');
  const isCancelled = implodedType.some((type: any) =>
    type?.includes('Cancelled')
  );

  const hasAttachment = item?.has_attachment;
  const isRejected = item?.confirmation_status?.id === 4;

  return (
    <>
      <Card
        sx={{
          width: '100%',
          position: 'relative',
          overflow: 'visible',
        }}
      >
        <Chip
          sx={{
            position: 'absolute',
            top: '-10px',
            right: 0,
            height: '20px',
          }}
          size={'small'}
          label={
            <>
              <Typography variant={'caption'}>
                {item?.confirmation_status?.name}
              </Typography>
            </>
          }
          color={
            item?.confirmation_status?.id
              ? item.confirmation_status.id === 1
                ? 'info'
                : item.confirmation_status.id === 2
                ? 'success'
                : item.confirmation_status.id === 3
                ? 'warning'
                : 'error'
              : 'default'
          }
        />
        <CardHeader
          title={
            <>
              <Stack direction={'row'} gap={1} alignItems={'center'} justifyContent={'space-between'}>
                <Typography>
                  Group Name:{' '}
                  <b
                    dangerouslySetInnerHTML={{ __html: item?.group }}
                  ></b>
                </Typography>
                {hasAttachment && (
                  <IconButton size={'small'} disabled>
                    <AttachFileRounded fontSize={'small'} color={'disabled'}/>
                  </IconButton>
                )}
                <MoreOptions
                  past={past}
                  isRejected={isRejected}
                  clickOnOpenChat={() => {
                    const rowData = { ...item };
                    rowData.id = item?.service_reservation_id;
                    onOpenChat(rowData);
                  }}
                  clickOnReject={() => {
                    setOpenConfirmDialog(item);
                  }}
                  clickOnOpenFileUpload={() => {
                    setOpenFileUploadDialog(item);
                  }}
                />
              </Stack>
            </>
          }
          subheader={item?.created_at}
          sx={{
            '@media (max-width:600px)': {
              p: '8px',
            },
          }}
        />
        <CardContent
          sx={{
            '@media (max-width:600px)': {
              padding: '0px 8px',
            },
          }}
        >
          <Typography variant="body1" sx={{ color: 'text.secondary' }}>
            <ConfirmedTable item={item} />
          </Typography>

          {item?.history && item?.history.length ? (
            <>
              <Accordion
                sx={{
                  background: '#f2f2f2',
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreRounded />}
                  aria-controls="panel1-content"
                  id="panel1-header"
                >
                  <Typography variant={'body2'}>Előzmények</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Stack gap={1}>
                    {item.history.map((subItem: any) => {
                      return (
                        <>
                          <Card
                            sx={{
                              width: '100%',
                            }}
                          >
                            <CardHeader
                              action={
                                <Chip
                                  label={subItem?.confirmation_status?.name}
                                  color={
                                    subItem?.confirmation_status?.id
                                      ? subItem.confirmation_status.id === 1
                                        ? 'info'
                                        : subItem.confirmation_status.id === 2
                                        ? 'success'
                                        : subItem.confirmation_status.id === 3
                                        ? 'warning'
                                        : 'error'
                                      : 'default'
                                  }
                                />
                              }
                              title={
                                <>
                                  Group Name:{' '}
                                  <b
                                    dangerouslySetInnerHTML={{
                                      __html: subItem?.formatted?.group,
                                    }}
                                  ></b>
                                </>
                              }
                              subheader={subItem?.created_at}
                            />
                            <CardContent>
                              <Typography
                                variant="body2"
                                sx={{ color: 'text.secondary' }}
                              >
                                <ConfirmationTable item={subItem} />
                              </Typography>
                            </CardContent>
                          </Card>
                        </>
                      );
                    })}
                  </Stack>
                </AccordionDetails>
              </Accordion>
            </>
          ) : (
            <></>
          )}
        </CardContent>
        <CardActions
          sx={{
            justifyContent: openOffer ? 'start' : 'end',
            alignItems: 'flex-start',
          }}
        ></CardActions>
      </Card>
    </>
  );
};
